import React from "react";
import { ArrowUp, ArrowDown, Edit, Trash2 } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Interface for FormField (matching the one in FormTemplateBuilder)
export interface FormField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: Array<{ label: string; value: string } | string>;
}

// Available field types
const fieldTypes = [
  { value: "text", label: "Text Field" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown Select" },
  { value: "radio", label: "Radio Buttons" },
  { value: "checkbox", label: "Checkboxes" },
  { value: "date", label: "Date Picker" },
  { value: "number", label: "Number Input" },
];

// Props for the FieldRow component
interface FieldRowProps {
  field: FormField;
  index: number;
  totalFields: number;
  onFieldChange: (index: number, field: FormField) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

const FieldRow: React.FC<FieldRowProps> = ({
  field,
  index,
  totalFields,
  onFieldChange,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: field.name });

  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Handle field name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(index, { ...field, name: e.target.value });
  };

  // Handle field label change
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(index, { ...field, label: e.target.value });
  };

  // Handle field type change
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    const updatedField = { ...field, type: newType };

    // Add options array for select/radio/checkbox fields, remove for others
    if (["select", "radio", "checkbox"].includes(newType)) {
      if (!updatedField.options) {
        updatedField.options = [];
      }
    } else {
      delete updatedField.options;
    }

    onFieldChange(index, updatedField);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      className={`border-b ${
        isDark
          ? "border-gray-700 hover:bg-gray-800"
          : "border-gray-200 hover:bg-gray-50"
      }`}
      ref={setNodeRef}
      style={style}
    >
      <td>
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab w-full text-center inline-block"
        >
          ⋮⋮
        </span>
      </td>
      <td className="px-4 py-2">
        <input
          value={field.name}
          onChange={handleNameChange}
          className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 sm:text-sm ${
            theme === "dark"
              ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400"
              : "border-gray-300 focus:border-blue-500"
          }`}
          placeholder="field_name"
        />
      </td>
      <td className="px-4 py-2">
        <input
          value={field.label}
          onChange={handleLabelChange}
          className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 sm:text-sm ${
            theme === "dark"
              ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400"
              : "border-gray-300 focus:border-blue-500"
          }`}
          placeholder="Field Label"
        />
      </td>
      <td className="px-4 py-2">
        <select
          value={field.type}
          onChange={handleTypeChange}
          className={`px-3 py-2 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
            isDark
              ? "bg-gray-700 border-gray-600 text-white"
              : "border-gray-300 text-gray-900"
          }`}
        >
          {fieldTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        {field.required ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            Required
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            Optional
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            className={`p-1 rounded ${
              index === 0
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700"
            }`}
            aria-label="Move up"
          >
            <ArrowUp size={16} />
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={index === totalFields - 1}
            className={`p-1 rounded ${
              index === totalFields - 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700"
            }`}
            aria-label="Move down"
          >
            <ArrowDown size={16} />
          </button>
          <button
            onClick={() => onEdit(index)}
            className="p-1 rounded text-yellow-600 hover:bg-yellow-100 dark:text-yellow-400 dark:hover:bg-gray-700"
            aria-label="Edit field"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => onDelete(index)}
            className="p-1 rounded text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-gray-700"
            aria-label="Delete field"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default FieldRow;
